import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { useEffect, useMemo } from 'react';

import { BrandMark } from './components/Brand.js';
import { ApiError } from './lib/api.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { useTheme } from './lib/theme.js';
import { router } from './router.js';
import { ForcedPasswordChangePage } from './routes/PublicAuthPages.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Erro de permissão ou de validação não melhora com repetição.
        if (error instanceof ApiError && error.status && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/** Tela de espera enquanto a sessão inicial é resolvida pelo refresh token. */
function BootScreen() {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg">
      <div className="flex flex-col items-center gap-3">
        <BrandMark size={48} />
        <p className="text-[13px] text-ink-3">Carregando…</p>
      </div>
    </div>
  );
}

/**
 * Mantém o tema do documento em dia com a preferência guardada e com a do
 * sistema. Montado na raiz para valer também nas telas sem casca (login).
 */
function ThemeSync() {
  useTheme();
  return null;
}

function RoutedApp() {
  const { user, loading, inPlatform, mustChangePassword } = useAuth();
  const isAuthenticated = Boolean(user);

  const context = useMemo(() => ({ isAuthenticated, inPlatform }), [isAuthenticated, inPlatform]);

  /*
   * Sessão perdida (ou recém-obtida) muda o resultado dos guardas de rota.
   * `invalidate` reavalia a rota atual e aplica o redirecionamento.
   */
  useEffect(() => {
    if (!loading) void router.invalidate();
  }, [isAuthenticated, inPlatform, loading]);

  if (loading) return <BootScreen />;

  /*
   * Senha padrão ainda de pé: a troca toma a tela inteira, no lugar do roteador.
   *
   * Trocar por um guarda de rota deixaria a casca montada e cada página
   * disparando consultas que o servidor recusa — a pessoa veria o painel
   * piscando erro atrás do formulário. Aqui não há rota nenhuma a alcançar,
   * que é exatamente o que a API está dizendo.
   */
  if (isAuthenticated && mustChangePassword) return <ForcedPasswordChangePage />;

  return <RouterProvider router={router} context={context} />;
}

export function App() {
  return (
    /*
     * `reducedMotion="user"`: quem pediu menos movimento ao sistema recebe só a
     * troca de opacidade — transforms e deslocamentos são desligados na
     * biblioteca inteira, sem que cada animação precise checar a preferência.
     */
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeSync />
          <RoutedApp />
        </AuthProvider>
      </QueryClientProvider>
    </MotionConfig>
  );
}
